// utils/exportProjectsToJson.js
//
// Client-side "download as JSON" helper. Generic on purpose so it can be
// reused anywhere a list of records needs to be exported, not just projects.
// No data leaves the browser; everything happens with a Blob + object URL.

/**
 * Serializes a list of records to a pretty-printed JSON file and triggers
 * a browser download. Preserves every field on each record exactly as
 * stored (no manual field allow-list), so newly added fields are included
 * automatically.
 *
 * @param {Array<object>} records - the full list of records to export
 * @param {object} [options]
 * @param {string} [options.filenamePrefix='projects'] - base name for the file
 * @param {boolean} [options.dateStamp=true] - append today's date (YYYY-MM-DD) to the filename
 * @returns {string} the filename that was downloaded
 */
export const exportProjectsToJson = (
  records,
  { filenamePrefix = 'projects', dateStamp = true } = {},
) => {
  const dataToExport = Array.isArray(records) ? records : []

  // Preserve the original structure as-is (id + inputValues + categories,
  // etc.) rather than hand-picking fields, so any field added to the data
  // model later is included automatically.
  const json = JSON.stringify(dataToExport, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  const suffix = dateStamp ? `-${new Date().toISOString().slice(0, 10)}` : ''
  const filename = `${filenamePrefix}${suffix}.json`

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Release the object URL on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 0)

  return filename
}

export default exportProjectsToJson